import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Collapse,
    IconButton,
    TablePagination
} from '@mui/material';
import {
    Security as SecurityIcon,
    Phone as PhoneIcon,
    Warning as WarningIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    ArrowBack as ArrowBackIcon,
    Person as PersonIcon
} from '@mui/icons-material';

function SDS() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [suspiciousPatterns, setSuspiciousPatterns] = useState([]);
    const [analysisResults, setAnalysisResults] = useState(null);
    const [expandedPattern, setExpandedPattern] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 25,
        total: 0,
        pages: 0
    });
    
    // Two-level navigation state
    const [patternView, setPatternView] = useState('agentList'); // 'agentList' or 'agentDetail'
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [groupedPatterns, setGroupedPatterns] = useState({});
    
    // Get current date in PST for default values
    const getCurrentPSTDate = () => {
        const now = new Date();
        const pstNow = new Date(now.toLocaleString("en-US", {timeZone: "America/Vancouver"}));
        return pstNow.toISOString().split('T')[0]; // Get YYYY-MM-DD in PST
    };
    
    const [filters, setFilters] = useState({
        dateFrom: getCurrentPSTDate(),
        dateTo: getCurrentPSTDate(),
        team: '',
        threshold: '5',
        minDuration: '',
        status: 'all'
    });

    // Group patterns by agent for card view
    const groupPatternsByAgent = (patterns) => {
        const grouped = patterns.reduce((acc, pattern) => {
            const agentKey = pattern.agent_id;
            if (!acc[agentKey]) {
                acc[agentKey] = {
                    agent_id: pattern.agent_id,
                    agent_name: pattern.agent_name,
                    patterns: [],
                    totalCalls: 0,
                    highestRisk: 0,
                    totalPatterns: 0
                };
            }
            
            acc[agentKey].patterns.push(pattern);
            acc[agentKey].totalCalls += pattern.call_count;
            acc[agentKey].highestRisk = Math.max(acc[agentKey].highestRisk, pattern.risk_score);
            acc[agentKey].totalPatterns++;
            
            return acc;
        }, {});
        
        return grouped;
    };

    const handleAnalyze = async (page = 1, limit = 1000) => { // Load more patterns for grouping
        setLoading(true);
        setError('');
        if (page === 1) {
            setSuspiciousPatterns([]);
            setAnalysisResults(null);
            setPatternView('agentList'); // Reset to agent list view
            setSelectedAgent(null);
        }
        
        try {
            // Build query parameters
            const params = new URLSearchParams();
            
            if (filters.team && filters.team !== '') {
                params.append('team', filters.team);
            }
            if (filters.dateFrom) {
                params.append('dateFrom', filters.dateFrom);
            }
            if (filters.dateTo) {
                params.append('dateTo', filters.dateTo);
            }
            if (filters.threshold) {
                params.append('threshold', filters.threshold);
            }
            if (filters.minDuration) {
                params.append('minDuration', filters.minDuration);
            }
            
            // Load all patterns for proper grouping (we'll paginate in the detail view)
            params.append('page', '1');
            params.append('limit', '1000');
            
            console.log('Calling suspicious patterns API with params:', params.toString());
            
            // Call the backend API
            const response = await fetch(`/api/calls/suspicious-patterns?${params.toString()}`);
            const data = await response.json();
            
            if (data.success) {
                setSuspiciousPatterns(data.patterns);
                setAnalysisResults(data.analysis);
                setPagination({
                    page: data.page,
                    limit: data.limit,
                    total: data.total,
                    pages: data.pages
                });
                
                // Group patterns by agent for card view
                const grouped = groupPatternsByAgent(data.patterns);
                setGroupedPatterns(grouped);
                
                console.log(`Analysis complete: Found ${data.total} suspicious patterns from ${Object.keys(grouped).length} agents`);
            } else {
                setError(data.error || 'Failed to analyze patterns');
            }
            
        } catch (err) {
            console.error('Error analyzing patterns:', err);
            setError('Failed to connect to analysis service');
        } finally {
            setLoading(false);
        }
    };

    // Navigation handlers
    const handleAgentClick = (agentData) => {
        setSelectedAgent(agentData);
        setPatternView('agentDetail');
        setPagination({ page: 1, limit: 25, total: agentData.patterns.length, pages: Math.ceil(agentData.patterns.length / 25) });
    };

    const handleBackToAgentList = () => {
        setPatternView('agentList');
        setSelectedAgent(null);
    };

    // Agent List View - Shows cards for each suspicious agent
    const renderAgentListView = () => (
        <Grid container spacing={3}>
            {Object.values(groupedPatterns)
                .sort((a, b) => b.highestRisk - a.highestRisk) // Sort by highest risk first
                .map((agentData) => (
                <Grid item xs={12} sm={6} md={4} key={agentData.agent_id}>
                    <Card 
                        sx={{ 
                            cursor: 'pointer',
                            '&:hover': { 
                                elevation: 4,
                                transform: 'translateY(-2px)',
                                transition: 'all 0.2s'
                            },
                            border: agentData.highestRisk >= 70 ? '2px solid #f44336' : 
                                   agentData.highestRisk >= 40 ? '2px solid #ff9800' : 
                                   '1px solid #e0e0e0'
                        }}
                        onClick={() => handleAgentClick(agentData)}
                    >
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <PersonIcon color="primary" sx={{ mr: 1, fontSize: 30 }} />
                                <Typography variant="h6" noWrap>
                                    {agentData.agent_name}
                                </Typography>
                            </Box>
                            
                            <Grid container spacing={1}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        Patterns:
                                    </Typography>
                                    <Chip 
                                        label={agentData.totalPatterns}
                                        size="small"
                                        color="warning"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        Total Calls:
                                    </Typography>
                                    <Chip 
                                        label={agentData.totalCalls}
                                        size="small"
                                        color="error"
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ mt: 1 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        Highest Risk:
                                    </Typography>
                                    <Chip 
                                        label={`${agentData.highestRisk}%`}
                                        size="medium"
                                        color={agentData.highestRisk >= 70 ? "error" : 
                                               agentData.highestRisk >= 40 ? "warning" : "info"}
                                        sx={{ mt: 0.5, fontWeight: 'bold' }}
                                    />
                                </Grid>
                            </Grid>
                            
                            {agentData.highestRisk >= 70 && (
                                <Alert severity="error" sx={{ mt: 2, py: 0 }}>
                                    <Typography variant="caption">
                                        High Risk - Immediate Review Needed
                                    </Typography>
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            ))}
            
            {Object.keys(groupedPatterns).length === 0 && !loading && (
                <Grid item xs={12}>
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="textSecondary">
                            No suspicious agents detected
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Try adjusting your filters or threshold settings
                        </Typography>
                    </Paper>
                </Grid>
            )}
        </Grid>
    );

    // Agent Detail View - Shows patterns for selected agent
    const renderAgentDetailView = () => {
        if (!selectedAgent) return null;
        
        // Pagination for detail view
        const startIndex = (pagination.page - 1) * pagination.limit;
        const endIndex = startIndex + pagination.limit;
        const paginatedPatterns = selectedAgent.patterns.slice(startIndex, endIndex);
        
        const handleDetailPageChange = (event, newPage) => {
            setPagination(prev => ({ ...prev, page: newPage }));
        };
        
        const handleDetailRowsPerPageChange = (event) => {
            const newLimit = parseInt(event.target.value, 10);
            setPagination(prev => ({ 
                ...prev, 
                limit: newLimit, 
                page: 1,
                pages: Math.ceil(selectedAgent.patterns.length / newLimit)
            }));
        };
        
        return (
            <Box>
                {/* Header with back button */}
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                        <IconButton onClick={handleBackToAgentList} sx={{ mr: 2 }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <PersonIcon color="primary" sx={{ mr: 1, fontSize: 30 }} />
                        <Typography variant="h5" sx={{ flexGrow: 1 }}>
                            {selectedAgent.agent_name} - Suspicious Patterns
                        </Typography>
                        <Box display="flex" gap={1}>
                            <Chip 
                                label={`${selectedAgent.totalPatterns} patterns`}
                                color="warning"
                            />
                            <Chip 
                                label={`${selectedAgent.totalCalls} total calls`}
                                color="error"
                            />
                            <Chip 
                                label={`${selectedAgent.highestRisk}% max risk`}
                                color={selectedAgent.highestRisk >= 70 ? "error" : "warning"}
                            />
                        </Box>
                    </Box>
                </Paper>
                
                {/* Patterns Table */}
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Pattern Details for {selectedAgent.agent_name}
                    </Typography>
                    
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell><strong>Number Called</strong></TableCell>
                                    <TableCell><strong>Call Count</strong></TableCell>
                                    <TableCell><strong>Avg Duration</strong></TableCell>
                                    <TableCell><strong>Risk Score</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedPatterns.map((pattern, index) => (
                                    <React.Fragment key={index}>
                                        <TableRow>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setExpandedPattern(expandedPattern === index ? null : index)}
                                                >
                                                    {expandedPattern === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>{pattern.callee_number}</TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={pattern.call_count} 
                                                    color={pattern.call_count >= 10 ? "error" : "warning"} 
                                                    size="small" 
                                                />
                                            </TableCell>
                                            <TableCell>{pattern.avg_duration}s</TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={`${pattern.risk_score}%`}
                                                    color={
                                                        pattern.risk_score >= 70 ? "error" : 
                                                        pattern.risk_score >= 40 ? "warning" : "info"
                                                    }
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label="Flagged" color="error" size="small" />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                                <Collapse in={expandedPattern === index} timeout="auto" unmountOnExit>
                                                    <Box sx={{ margin: 1 }}>
                                                        <Typography variant="h6" gutterBottom component="div">
                                                            Call Details
                                                        </Typography>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Call ID</TableCell>
                                                                    <TableCell>Duration</TableCell>
                                                                    <TableCell>Result</TableCell>
                                                                    <TableCell>Time</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {pattern.calls.slice(0, 10).map((call, callIndex) => (
                                                                    <TableRow key={callIndex}>
                                                                        <TableCell>{call.call_id}</TableCell>
                                                                        <TableCell>{call.duration}s</TableCell>
                                                                        <TableCell>{call.result}</TableCell>
                                                                        <TableCell>
                                                                            {new Date(call.call_time).toLocaleString('en-US', {
                                                                                timeZone: 'America/Vancouver',
                                                                                month: 'short',
                                                                                day: 'numeric',
                                                                                hour: '2-digit',
                                                                                minute: '2-digit'
                                                                            })}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                        {pattern.calls.length > 10 && (
                                                            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                                                                Showing 10 of {pattern.calls.length} calls
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {/* Pagination */}
                    {selectedAgent.patterns.length > 0 && (
                        <TablePagination
                            component="div"
                            count={selectedAgent.patterns.length}
                            page={pagination.page - 1}
                            onPageChange={handleDetailPageChange}
                            rowsPerPage={pagination.limit}
                            onRowsPerPageChange={handleDetailRowsPerPageChange}
                            rowsPerPageOptions={[10, 25, 50]}
                            labelDisplayedRows={({ from, to, count }) => 
                                `${from}-${to} of ${count} patterns for ${selectedAgent.agent_name}`
                            }
                            labelRowsPerPage="Patterns per page:"
                        />
                    )}
                </Paper>
            </Box>
        );
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" gutterBottom>
                    ☹️ SAD - Suspicious Activity Detection
                </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                📊 Suspicious Activity Detection monitors call patterns to identify unusual call patterns.
            </Alert>

            {/* Filters Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                    <FilterIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">
                        Detection Filters
                    </Typography>
                </Box>
                
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            label="Date From"
                            type="date"
                            value={filters.dateFrom}
                            onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            label="Date To"
                            type="date"
                            value={filters.dateTo}
                            onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <Select
                                value={filters.team}
                                onChange={(e) => setFilters({...filters, team: e.target.value})}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    <em>Select a Team</em>
                                </MenuItem>
                                <MenuItem value="all">All Teams</MenuItem>
                                <MenuItem value="1">Mahmoud's Team</MenuItem>
                                <MenuItem value="3">Vantaa-Team</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            label="Call Threshold"
                            type="number"
                            value={filters.threshold}
                            onChange={(e) => setFilters({...filters, threshold: e.target.value})}
                            size="small"
                            placeholder="e.g. 5"
                            inputProps={{ min: 1, max: 50 }}
                            helperText="Min calls to flag"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            label="Min Duration (sec)"
                            type="number"
                            value={filters.minDuration}
                            onChange={(e) => setFilters({...filters, minDuration: e.target.value})}
                            size="small"
                            placeholder="e.g. 10"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                            onClick={handleAnalyze}
                            disabled={loading}
                            sx={{ height: '40px' }}
                        >
                            {loading ? 'Analyzing...' : 'Analyze'}
                        </Button>
                    </Grid>
                </Grid>
                
                <Box mt={2}>
                    <Chip label="Real-time monitoring active" color="success" size="small" sx={{ mr: 1 }} />
                    <Chip label={`Detection threshold: ${filters.threshold}+ calls`} color="info" size="small" sx={{ mr: 1 }} />
                    <Chip label="PST timezone" color="primary" size="small" sx={{ mr: 1 }} />
                    <Chip label="Last scan: 2 minutes ago" color="default" size="small" />
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {/* System Overview */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom color="primary">
                            Detection Overview
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="between">
                                    <Box>
                                        <Typography variant="h4" color="warning.main">
                                            {pagination.total || suspiciousPatterns.length}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {pagination.total > 0 ? `Flagged Patterns (${suspiciousPatterns.length} shown)` : 'Flagged Patterns'}
                                        </Typography>
                                    </Box>
                                    <WarningIcon color="warning" sx={{ fontSize: 40, ml: 2 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="between">
                                    <Box>
                                        <Typography variant="h4" color="error">
                                            {new Set(suspiciousPatterns.map(p => p.agent_id)).size}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Suspicious Agents
                                        </Typography>
                                    </Box>
                                    <SecurityIcon color="error" sx={{ fontSize: 40, ml: 2 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="between">
                                    <Box>
                                        <Typography variant="h4" color="info.main">
                                            {new Set(suspiciousPatterns.map(p => p.callee_number)).size}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Monitored Numbers
                                        </Typography>
                                    </Box>
                                    <PhoneIcon color="info" sx={{ fontSize: 40, ml: 2 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="between">
                                    <Box>
                                        <Typography variant="h4" color="success.main">
                                            Active
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            System Status
                                        </Typography>
                                    </Box>
                                    <SecurityIcon color="success" sx={{ fontSize: 40, ml: 2 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Detection Results - Agent Cards or Detail View */}
                    <Grid item xs={12}>
                        {patternView === 'agentList' ? (
                            <Paper sx={{ p: 3, mt: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Suspicious Agents Detected
                                </Typography>
                                
                                {Object.keys(groupedPatterns).length === 0 ? (
                                    <Typography variant="body1" color="textSecondary">
                                        No unusual call patterns detected. SAD is actively monitoring call activities for unusual call patterns and frequency violations.
                                    </Typography>
                                ) : (
                                    renderAgentListView()
                                )}
                            </Paper>
                        ) : (
                            renderAgentDetailView()
                        )}
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}

export default SDS;